import { Navigate, useParams } from "react-router";
import { updateTemplateAction } from "@/actions/templates";
import { useApiQuery } from "@/api/useApiQuery";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import type {
  RungRow,
  SkillListRow,
  TopicListRow,
  TraitListRow,
} from "@shared/dto";
import { formatQuestAssignment } from "@shared/questAssignment";
import type { Violation } from "@shared/publishGate";
import { PublishPanel } from "./quests/templates/PublishPanel";
import { RungLadder } from "./quests/templates/RungLadder";
import { TemplateForm } from "./quests/templates/TemplateForm";
import { WordingForm } from "./quests/templates/WordingForm";
import styles from "./quests/templates/TemplatePage.module.css";

const SKILL_STATUS_ACTIVE = "ACTIVE";
const TRAIT_STATUS_ACTIVE = "ACTIVE";

type MetricRow = {
  id: number;
  code: string;
  unit: string;
  agg: string;
  isActive: boolean;
};

type LocaleRow = { code: string; isDefault: boolean };

type TemplateRow = {
  id: number;
  code: string | null;
  goalType: string;
  metricId: number | null;
  verification: string;
  topicId: number | null;
  participation: string[];
  setting: string;
  tags: string[];
  cooldownDays: number | null;
  contentGrade: string;
  hasOpenSubject: boolean;
  status: string;
};

type TranslationRow = {
  locale: string;
  title: string | null;
  assignmentTemplate: string | null;
};

type GateResult = { canPublish: boolean; violations: Violation[] };

export const TemplatePage = () => {
  const { id } = useParams();
  const templateId = Number(id);
  const isIdUsable = Number.isInteger(templateId) && templateId > 0;
  // A path that cannot name a row is not worth a request; the listing is the
  // only place to go from here.
  const path = isIdUsable ? `/templates/${templateId}` : "/templates/0";

  const template = useApiQuery<TemplateRow>(path);
  const metrics = useApiQuery<MetricRow[]>("/metrics");
  const topics = useApiQuery<TopicListRow[]>("/topics");
  const skills = useApiQuery<SkillListRow[]>("/skills");
  const traits = useApiQuery<TraitListRow[]>("/traits");
  const locales = useApiQuery<LocaleRow[]>("/locales");
  const rungs = useApiQuery<RungRow[]>(`${path}/quests`);
  const boundSkills = useApiQuery<{ skillIds: number[] }>(`${path}/skills`);
  const boundTraits = useApiQuery<{
    requiresTraitIds: number[];
    grantsTraitIds: number[];
  }>(`${path}/traits`);
  const translations = useApiQuery<TranslationRow[]>(`${path}/translations`);
  const gate = useApiQuery<GateResult>(`${path}/publish`);

  if (!isIdUsable || template.error) {
    return <Navigate to="/quests" replace />;
  }

  const isLoading =
    template.isLoading ||
    metrics.isLoading ||
    topics.isLoading ||
    skills.isLoading ||
    traits.isLoading ||
    locales.isLoading ||
    rungs.isLoading ||
    boundSkills.isLoading ||
    boundTraits.isLoading ||
    translations.isLoading ||
    gate.isLoading;

  if (isLoading || !template.data) {
    return (
      <>
        <PageHeader kicker="Quest template" title="Loading..." />
      </>
    );
  }

  const defaultLocale =
    (locales.data ?? []).find((locale) => locale.isDefault)?.code ?? "en";
  const wording = (translations.data ?? []).find(
    (row) => row.locale === defaultLocale,
  );
  const title = wording?.title ?? "";
  const assignmentTemplate = wording?.assignmentTemplate ?? "";
  const rungRows = rungs.data ?? [];

  // The rungs feed both the ICU preview and the ladder's own headings, so each
  // one is rendered once here rather than in two components. The title is not
  // rendered: it holds no placeholder, so every rung would repeat it verbatim.
  const renderedAssignments = Object.fromEntries(
    rungRows.map((rung) => [
      rung.id,
      formatQuestAssignment({
        assignmentTemplate,
        locale: defaultLocale,
        targetValue: rung.targetValue,
        requiredDays: rung.requiredDays,
      }),
    ]),
  );

  const bindings = rungRows
    .filter((rung) => rung.status === "ACTIVE")
    .map((rung) => ({
      id: rung.id,
      target: rung.targetValue === null ? null : Number(rung.targetValue),
      days: rung.requiredDays,
    }));

  const row = template.data;

  return (
    <>
      <PageHeader
        kicker="Quest template"
        title={title === "" ? "Untitled" : title}
        action={<StatusBadge label={row.status} tone="outline" />}
      />

      <div className={styles.stack}>
        <WordingForm
          templateId={templateId}
          localeCode={defaultLocale}
          title={title}
          assignment={assignmentTemplate}
          bindings={bindings}
        />

        <TemplateForm
          action={updateTemplateAction.bind(null, templateId)}
          values={{
            code: row.code ?? "",
            goalType: row.goalType,
            metricId: row.metricId === null ? "" : String(row.metricId),
            verification: row.verification,
            topicId: row.topicId === null ? "" : String(row.topicId),
            participation: row.participation,
            setting: row.setting,
            tags: row.tags.join(", "),
            cooldownDays:
              row.cooldownDays === null ? "" : String(row.cooldownDays),
            contentGrade: row.contentGrade,
            hasOpenSubject: row.hasOpenSubject,
            title: "",
            assignment: "",
            skillIds: boundSkills.data?.skillIds ?? [],
            requiresTraitIds: boundTraits.data?.requiresTraitIds ?? [],
            grantsTraitIds: boundTraits.data?.grantsTraitIds ?? [],
          }}
          metricOptions={(metrics.data ?? [])
            .filter((metric) => metric.isActive || metric.id === row.metricId)
            .map((metric) => ({
              value: String(metric.id),
              label: `${metric.code} (${metric.unit}, ${metric.agg})`,
            }))}
          topics={(topics.data ?? [])
            .filter((topic) => topic.isActive || topic.id === row.topicId)
            .map((topic) => ({
              id: topic.id,
              code: topic.code,
              label: topic.label,
            }))}
          localeCode={defaultLocale}
          bindings={bindings}
          hasRungs={rungRows.length > 0}
          skills={(skills.data ?? [])
            .filter((skill) => skill.status === SKILL_STATUS_ACTIVE)
            .map((skill) => ({
              id: skill.id,
              code: skill.code,
              label: skill.label,
              description: skill.description,
            }))}
          traits={(traits.data ?? []).map((trait) => ({
            id: trait.id,
            code: trait.code,
            label: trait.label,
            isArchived: trait.status !== TRAIT_STATUS_ACTIVE,
          }))}
          isWordingIncluded={false}
          submitLabel="Save template"
          pendingLabel="Saving..."
        />

        <RungLadder
          templateId={templateId}
          goalType={row.goalType}
          rungs={rungRows}
          renderedAssignments={renderedAssignments}
          hasMetric={row.metricId !== null}
          onChanged={rungs.refresh}
        />

        <PublishPanel
          templateId={templateId}
          status={row.status}
          violations={gate.data?.violations ?? []}
        />
      </div>
    </>
  );
};
